import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  ChevronDown,
  ChevronRight,
  Folder,
  AlertTriangle
} from 'lucide-react';
import { getOrganizationalUnits, createOrganizationalUnit, deleteOrganizationalUnit, getUsers, getChromeDeviceCounts, testChromeDeviceAPI } from '../firebase';
import { useUser } from '../contexts/UserContext';
import { filterDataByPermissions, validateAction } from '../utils/permissions';

const OrganizationalUnits = () => {
  const { hasPermission, canAccess, userOUs, userPermissions } = useUser();
  const [organisationalUnits, setOrganisationalUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [deviceCounts, setDeviceCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 3, step: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedOUs, setExpandedOUs] = useState(new Set());
  const [newOU, setNewOU] = useState({
    name: '',
    description: '',
    parentPath: ''
  });
  const [selectedParentOU, setSelectedParentOU] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchOrganizationalUnits();
  }, []);

  const fetchOrganizationalUnits = async () => {
    try {
      setLoading(true);
      setLoadingProgress({ current: 0, total: 3, step: 'Starting data fetch...' });
      
      // Fetch OUs and users first
      setLoadingProgress({ current: 1, total: 3, step: 'Loading organisational units...' });
      const [ouResponse, usersResponse] = await Promise.all([
        getOrganizationalUnits(),
        getUsers()
      ]);
      
      // Fetch device counts separately with longer timeout
      setLoadingProgress({ current: 2, total: 3, step: 'Loading device counts...' });
      let deviceResponse = { data: { success: true, data: {} } };
      try {
        console.log('Fetching device counts...');
        deviceResponse = await getChromeDeviceCounts();
        console.log('Device counts fetched successfully');
      } catch (error) {
        console.log('Chrome device counts not available:', error.message);
        deviceResponse = { data: { success: true, data: {} } };
      }
      
      // Process users data
      let usersData = [];
      if (usersResponse.data?.success && usersResponse.data.data) {
        usersData = usersResponse.data.data;
        setUsers(usersData);
      }
      
      // Process device counts
      let deviceCountsData = {};
      if (deviceResponse.data?.success && deviceResponse.data.data) {
        deviceCountsData = deviceResponse.data.data;
        console.log('🔧 Device counts received:', Object.keys(deviceCountsData).length, 'OUs with devices');
        console.log('🔧 Sample device counts:', Object.entries(deviceCountsData).slice(0, 5));
        setDeviceCounts(deviceCountsData);
      } else {
        console.log('❌ Device counts failed or empty:', deviceResponse);
      }
      
      // Count users per OU
      const userCountsByOU = {};
      usersData.forEach(user => {
        const ouPath = user.orgUnitPath || '/';
        userCountsByOU[ouPath] = (userCountsByOU[ouPath] || 0) + 1;
      });
      
      setLoadingProgress({ current: 3, total: 3, step: 'Processing data...' });
      
      if (ouResponse.data.success) {
        console.log('🏢 Raw OU data received:', ouResponse.data.data.length, ouResponse.data.data);
        console.log('🏢 Sample OU paths:', ouResponse.data.data.slice(0, 5).map(ou => ou.orgUnitPath));
        console.log('👤 User permissions:', userPermissions);
        console.log('👤 User OUs:', userOUs);
        
        // Transform the API response to match our UI structure
        const transformedOUs = ouResponse.data.data.map(ou => {
          const deviceCount = deviceCountsData[ou.orgUnitPath] || 0;
          if (deviceCount > 0) {
            console.log(`🔧 OU ${ou.orgUnitPath} has ${deviceCount} devices`);
          }
          return {
            id: ou.orgUnitPath, // use path as canonical id for tree
            name: ou.name,
            description: ou.description || '',
            parentId: ou.parentOrgUnitPath || null,
            userCount: userCountsByOU[ou.orgUnitPath] || 0,
            deviceCount: deviceCount,
            children: []
          };
        });
        
        console.log('🔄 Transformed OUs:', transformedOUs.length, transformedOUs);
        
        // Debug: Check if any OUs have device counts
        const ousWithDevices = transformedOUs.filter(ou => ou.deviceCount > 0);
        console.log('🔧 OUs with devices:', ousWithDevices.length, ousWithDevices.slice(0, 5));
        
        // Filter OUs based on user permissions
        const accessibleOUs = filterDataByPermissions(transformedOUs, userPermissions, userOUs);
        console.log('🔒 Filtered accessible OUs:', accessibleOUs.length, accessibleOUs);
        
        // Build hierarchical structure
        const ouMap = {};
        const rootOUs = [];
        
        accessibleOUs.forEach(ou => {
          ouMap[ou.id] = ou;
        });
        
        accessibleOUs.forEach(ou => {
          if (ou.parentId && ouMap[ou.parentId]) {
            if (!ouMap[ou.parentId].children) {
              ouMap[ou.parentId].children = [];
            }
            ouMap[ou.parentId].children.push(ou);
          } else {
            rootOUs.push(ou);
          }
        });
        
        setOrganisationalUnits(rootOUs);
      } else {
        throw new Error('Failed to fetch organisational units');
      }
    } catch (error) {
      console.error('Error fetching organisational units:', error);
      // Fallback to mock data if API fails
      const mockData = [
        {
          id: '1',
          name: 'Root',
          description: 'Root organisational unit',
          parentId: null,
          userCount: 245,
          deviceCount: 0,
          children: [
            {
              id: '2',
              name: 'Sales',
              description: 'Sales department',
              parentId: '1',
              userCount: 45,
              deviceCount: 12,
              children: [
                {
                  id: '3',
                  name: 'Enterprise Sales',
                  description: 'Enterprise sales team',
                  parentId: '2',
                  userCount: 12,
                  deviceCount: 5,
                  children: []
                },
                {
                  id: '4',
                  name: 'SMB Sales',
                  description: 'Small and medium business sales',
                  parentId: '2',
                  userCount: 33,
                  deviceCount: 7,
                  children: []
                }
              ]
            },
            {
              id: '5',
              name: 'Marketing',
              description: 'Marketing department',
              parentId: '1',
              userCount: 28,
              deviceCount: 0,
              children: []
            },
            {
              id: '6',
              name: 'Engineering',
              description: 'Engineering department',
              parentId: '1',
              userCount: 89,
              deviceCount: 25,
              children: [
                {
                  id: '7',
                  name: 'Frontend',
                  description: 'Frontend development team',
                  parentId: '6',
                  userCount: 23,
                  deviceCount: 8,
                  children: []
                },
                {
                  id: '8',
                  name: 'Backend',
                  description: 'Backend development team',
                  parentId: '6',
                  userCount: 31,
                  deviceCount: 12,
                  children: []
                },
                {
                  id: '9',
                  name: 'DevOps',
                  description: 'DevOps team',
                  parentId: '6',
                  userCount: 15,
                  deviceCount: 5,
                  children: []
                }
              ]
            }
          ]
        }
      ];
      setOrganisationalUnits(mockData);
    } finally {
      setLoading(false);
    }
  };

  // Get all OU paths for dropdown
  const getAllOUPaths = (ous, prefix = '') => {
    const paths = [];
    ous.forEach(ou => {
      // Use the actual orgUnitPath (ou.id) instead of building from name
      const fullPath = ou.id || (prefix + ou.name);
      paths.push({ value: fullPath, label: `${ou.name} (${fullPath})` });
      if (ou.children && ou.children.length > 0) {
        paths.push(...getAllOUPaths(ou.children, fullPath + '/'));
      }
    });
    return paths;
  };

  const handleCreateOU = async (e) => {
    e.preventDefault();
    
    const parentPath = selectedParentOU || newOU.parentPath || '/';
    
    // Check permissions
    const validation = validateAction('ou_management', parentPath, userPermissions, userOUs);
    if (!validation.allowed) {
      alert(`Cannot create OU: ${validation.reason}`);
      return;
    }
    
    try {
      console.log('Creating OU with:', {
        name: newOU.name,
        description: newOU.description,
        parentPath: parentPath
      });
      
      // Call the real Firebase Function
      const response = await createOrganizationalUnit({
        name: newOU.name,
        description: newOU.description,
        parentPath: parentPath
      });
      
      if (response.data.success) {
        console.log('OU created successfully:', response.data.data);
        setShowCreateModal(false);
        setNewOU({ name: '', description: '', parentPath: '' });
        setSelectedParentOU('');
        // Refresh the list
        fetchOrganizationalUnits();
      } else {
        throw new Error(response.data.message || 'Failed to create organisational unit');
      }
    } catch (error) {
      console.error('Error creating OU:', error);
      alert(`Failed to create organisational unit: ${error.message}`);
    }
  };

  const handleDeleteOU = async (ouId) => {
    // Check permissions
    const validation = validateAction('ou_management', ouId, userPermissions, userOUs);
    if (!validation.allowed) {
      alert(`Cannot delete OU: ${validation.reason}`);
      return;
    }
    
    // Find the OU to get its name and user count for better messaging
    const findOU = (ous, targetId) => {
      for (const ou of ous) {
        if (ou.id === targetId) return ou;
        if (ou.children) {
          const found = findOU(ou.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };
    
    const targetOU = findOU(organisationalUnits, ouId);
    const userCount = targetOU?.userCount || 0;
    const deviceCount = targetOU?.deviceCount || 0;
    
    // Show appropriate confirmation message based on user and device count
    let confirmMessage = 'Are you sure you want to delete this organisational unit?';
    if (userCount > 0 || deviceCount > 0) {
      const items = [];
      if (userCount > 0) items.push(`${userCount} user(s)`);
      if (deviceCount > 0) items.push(`${deviceCount} device(s)`);
      
      confirmMessage = `This organisational unit contains ${items.join(' and ')}. Deleting it will move these items to the root OU. Are you sure you want to continue?`;
    }
    
    if (window.confirm(confirmMessage)) {
      try {
        // Call the real Firebase Function
        const response = await deleteOrganizationalUnit({
          orgUnitPath: ouId
        });
        
        if (response.data.success) {
          // Refresh the list
          fetchOrganizationalUnits();
        } else {
          throw new Error('Failed to delete organisational unit');
        }
      } catch (error) {
        console.error('Error deleting OU:', error);
        
        // Handle specific error types with better messaging
        if (error.code === 'functions/failed-precondition') {
          alert(`Cannot delete organisational unit:\n\n${error.message}\n\nPlease move or delete all users and devices before deleting this OU.`);
        } else if (error.code === 'functions/permission-denied') {
          alert(`Permission denied: ${error.message}`);
        } else {
          alert(`Failed to delete organisational unit: ${error.message}`);
        }
      }
    }
  };

  const toggleExpanded = (ouId) => {
    const newExpanded = new Set(expandedOUs);
    if (newExpanded.has(ouId)) {
      newExpanded.delete(ouId);
    } else {
      newExpanded.add(ouId);
    }
    setExpandedOUs(newExpanded);
  };

  const openCreateModal = (parentOU = '') => {
    // parentOU should be the OU ID (orgUnitPath), not the name
    setSelectedParentOU(parentOU);
    setNewOU({ name: '', description: '', parentPath: parentOU });
    setShowCreateModal(true);
  };

  const handleTestChromeDeviceAPI = async () => {
    try {
      setLoading(true);
      console.log('Testing Chrome Device API...');
      
      const response = await testChromeDeviceAPI();
      
      if (response.data?.success) {
        const data = response.data.data;
        let message = `Chrome Device API Test Results:\n\n`;
        message += `Basic API Access: ${data.basicApiAccess ? '✅ Yes' : '❌ No'}\n`;
        message += `Organisational Units: ${data.organisationalUnits}\n`;
        message += `Customer ID: ${data.customerId}\n\n`;
        
        if (data.chromeDeviceResult) {
          message += `Chrome Device API: ✅ Working\n`;
          message += `Total Devices Found: ${data.chromeDeviceResult.totalDevices}\n`;
          message += `Message: ${data.chromeDeviceResult.message}\n\n`;
        } else if (data.chromeDeviceError) {
          message += `Chrome Device API: ❌ Failed\n`;
          message += `Error: ${data.chromeDeviceError.error}\n`;
          message += `Suggestion: ${data.chromeDeviceError.suggestion}\n\n`;
        }
        
        if (data.totalDevices !== undefined) {
          message += `Total Chrome Devices: ${data.totalDevices}\n`;
        }
        
        if (data.ouTestResults && Object.keys(data.ouTestResults).length > 0) {
          message += `OU Test Results:\n`;
          for (const [ou, count] of Object.entries(data.ouTestResults)) {
            message += `- ${ou}: ${count}\n`;
          }
        }
        
        alert(message);
      } else {
        throw new Error('Test failed');
      }
    } catch (error) {
      console.error('Error testing Chrome Device API:', error);
      alert(`Failed to test Chrome Device API: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderOU = (ou, level = 0) => {
    const hasChildren = ou.children && ou.children.length > 0;
    const isExpanded = expandedOUs.has(ou.id);
    const canManage = hasPermission('ou_management') && canAccess(ou.id);
    const hasUsers = ou.userCount > 0;
    const hasDevices = ou.deviceCount > 0;
    const canDelete = canManage && !hasUsers && !hasDevices; // Cannot delete if has users or devices
    
    // Debug: Log device count for OUs that should have devices
    if (ou.deviceCount > 0) {
      console.log(`🔧 Rendering OU ${ou.name} with ${ou.deviceCount} devices`);
    }

    return (
      <div key={ou.id} className="border-l-2 border-gray-200 ml-4">
        <div 
          className={`flex items-center justify-between p-4 hover:bg-gray-50 ${
            level === 0 ? 'bg-white' : 'bg-gray-50'
          }`}
        >
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              {hasChildren && (
                <button
                  onClick={() => toggleExpanded(ou.id)}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  {isExpanded ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </button>
              )}
              <Folder size={20} className="text-blue-500" />
              <div>
                <h3 className="font-medium text-gray-900">{ou.name}</h3>
                <p className="text-sm text-gray-600">{ou.description || ou.id}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <Users size={16} />
                <span>{ou.userCount} users</span>
              </div>
              <div className="flex items-center space-x-1">
                <Building2 size={16} />
                <span>{ou.deviceCount} devices</span>
              </div>
              {(hasUsers || hasDevices) && (
                <div className="flex items-center space-x-1 text-amber-600" title="Cannot delete OU with users or devices">
                  <AlertTriangle size={14} />
                  <span className="text-xs">Protected</span>
                </div>
              )}
            </div>
            
            {canManage ? (
              <div className="flex space-x-2">
                <button 
                  onClick={() => openCreateModal(ou.id)}
                  className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded"
                  title="Create Sub-OU"
                >
                  <Plus size={16} />
                </button>
                <button 
                  onClick={() => handleDeleteOU(ou.id)}
                  className={`p-2 rounded ${
                    canDelete 
                      ? 'text-gray-600 hover:text-red-600 hover:bg-red-50' 
                      : 'text-gray-400 cursor-not-allowed'
                  }`}
                  title={canDelete ? "Delete OU" : "Cannot delete OU with users or devices"}
                  disabled={!canDelete}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <AlertTriangle size={16} />
                <span>Read-only</span>
              </div>
            )}
          </div>
        </div>
        
        {hasChildren && isExpanded && (
          <div className="ml-4">
            {ou.children.map(child => renderOU(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Organisational Units</h1>
            <p className="text-gray-600 mt-2">Manage your domain's organisational structure</p>
          </div>
        </div>

        {/* Processing Notice - shown during loading */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-900">⚠️ Processing Notice</h3>
              <p className="text-sm text-blue-700 mt-1">
                <strong>Loading organisational units...</strong> This WILL take a few minutes to process and appear in your Google Workspace. 
                Please be patient while the data loads and propagates through the system.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-2 border-primary-600 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-gray-600 mb-2">{loadingProgress.step}</p>
            <div className="w-64 bg-gray-200 rounded-full h-2 mx-auto">
              <div 
                className="bg-primary-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${(loadingProgress.current / loadingProgress.total) * 100}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Step {loadingProgress.current} of {loadingProgress.total}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // filter by search
  const filterTree = (nodes) => {
    if (!search) return nodes;
    const match = (text) => text?.toLowerCase().includes(search.toLowerCase());
    const walk = (node) => {
      const childMatches = (node.children || []).map(walk).filter(Boolean);
      const selfMatches = match(node.name) || match(node.description) || match(node.id);
      if (selfMatches || childMatches.length) {
        return { ...node, children: childMatches };
      }
      return null;
    };
    return nodes.map(walk).filter(Boolean);
  };

  const filteredOUs = filterTree(organisationalUnits);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Organisational Units</h1>
          <p className="text-gray-600 mt-2">Manage your domain's organisational structure</p>
        </div>
        <div className="flex items-center space-x-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search OUs..."
            className="input-field w-64"
          />
          <div className="flex space-x-3">
            {/* Test Device API Button - Commented out per cleanup request */}
            {/* 
            <button
              onClick={handleTestChromeDeviceAPI}
              className="btn-secondary flex items-center space-x-2"
            >
              <Building2 size={16} />
              <span>Test Device API</span>
            </button>
            */}
            {hasPermission('ou_management') && (
            <button
              onClick={() => openCreateModal()}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus size={16} />
              <span>Create OU</span>
            </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
          <div className="space-y-2">
            {filteredOUs.length ? filteredOUs.map(ou => renderOU(ou)) : (
              <div className="text-sm text-gray-500">No OUs match your search.</div>
            )}
          </div>
      </div>

      {/* Create OU Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {selectedParentOU && selectedParentOU !== '/' 
                ? `Create Sub-OU under ${selectedParentOU}` 
                : 'Create Organisational Unit'
              }
            </h2>
            
            <form onSubmit={handleCreateOU} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={newOU.name}
                  onChange={(e) => setNewOU({ ...newOU, name: e.target.value })}
                  className="input-field"
                  placeholder="Enter OU name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={newOU.description}
                  onChange={(e) => setNewOU({ ...newOU, description: e.target.value })}
                  className="input-field"
                  rows="3"
                  placeholder="Enter description"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Parent OU
                </label>
                <select
                  value={selectedParentOU}
                  onChange={(e) => {
                    setSelectedParentOU(e.target.value);
                    setNewOU({ ...newOU, parentPath: e.target.value });
                  }}
                  className="input-field"
                  required
                >
                  <option value="/">Root (/)</option>
                  {getAllOUPaths(organisationalUnits).map(ou => (
                    <option key={ou.value} value={ou.value}>
                      {ou.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Selected parent: {selectedParentOU || '/'}
                </p>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  Create OU
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationalUnits; 